import React, { useState } from 'react';
import { Link, router } from '@inertiajs/react';
import Layout from '../../../components/Layout.jsx';

function fmt(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function SuperadminWebinarsShow({ webinar, registrations = [], invitations = [], broadcast_recording_available = false, chat_transcript_url = null }) {
  const [selected, setSelected] = useState(new Set());
  const [minutes, setMinutes] = useState('');

  const activeRegs = registrations.filter((r) => !r.cancelled_at);

  const toggle = (userId) => {
    const next = new Set(selected);
    if (next.has(userId)) next.delete(userId);
    else next.add(userId);
    setSelected(next);
  };

  const allSelected = activeRegs.length > 0 && activeRegs.every((r) => selected.has(r.user_id));
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(activeRegs.map((r) => r.user_id)));
  };

  const submitMark = (action) => {
    if (selected.size === 0) return;
    router.post(`/superadmin/webinars/${webinar.id}/${action}`, {
      user_ids: Array.from(selected),
      attendance_minutes: minutes || null,
    }, { preserveScroll: true, onSuccess: () => setSelected(new Set()) });
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-6 sm:p-8 space-y-6">
        <Link href="/superadmin/webinars" className="text-sm text-indigo-600 hover:text-indigo-700">← Back to webinars</Link>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{webinar.title}</h1>
              {webinar.instructor && <p className="text-sm text-gray-500 mt-0.5">Hosted by {webinar.instructor}</p>}
            </div>
            <Link
              href={`/superadmin/webinars/${webinar.id}/edit`}
              className="px-3 py-1.5 text-sm font-semibold rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
            >
              Edit
            </Link>
          </div>

          <dl className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <Field label="Starts">{fmt(webinar.starts_at)}</Field>
            <Field label="Ends">{fmt(webinar.ends_at)}</Field>
            <Field label="Credit hours">{webinar.credit_hours}</Field>
            <Field label="Status">{webinar.status}</Field>
            <Field label="Visibility">
              <span className="capitalize">{webinar.visibility || 'public'}</span>
              {webinar.visibility === 'private' && (
                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-800 border border-amber-200">Invite-only</span>
              )}
            </Field>
            <Field label="Registered">{webinar.registered_count}{webinar.capacity != null ? ` / ${webinar.capacity}` : ''}</Field>
            <Field label="Seats remaining">{webinar.seats_remaining ?? 'Unlimited'}</Field>
            <Field label="Delivery">{webinar.delivery_mode === 'hls' ? 'Self-hosted (HLS)' : 'External link'}</Field>
            <Field label="Broadcast">
              <BroadcastBadge state={webinar.broadcast_state} />
            </Field>
            {webinar.delivery_mode !== 'hls' && (
              <Field label="Join URL">{webinar.join_url ? <a href={webinar.join_url} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline truncate inline-block max-w-full">Open link</a> : '—'}</Field>
            )}
          </dl>
          {webinar.description && <p className="mt-4 text-sm text-gray-700 whitespace-pre-line">{webinar.description}</p>}
        </div>

        {webinar.delivery_mode === 'hls' && (
          <StreamingPanel webinar={webinar} />
        )}

        {webinar.visibility === 'private' && (
          <InvitedUsersPanel webinar={webinar} invitations={invitations} />
        )}

        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Registrations</h2>
              <p className="text-sm text-gray-500">Select attendees and mark them attended to award credit.</p>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
                <Link
                  href={`/superadmin/webinars/${webinar.id}/attendance`}
                  className="text-sm font-semibold text-indigo-600 hover:text-indigo-700"
                >
                  View engagement details →
                </Link>
                {broadcast_recording_available && (
                  <Link
                    href={`/superadmin/webinars/${webinar.id}/broadcast_recording`}
                    className="text-sm font-semibold text-indigo-600 hover:text-indigo-700"
                  >
                    Watch broadcast recording →
                  </Link>
                )}
                {/* Plain anchor, not Inertia Link: this is a file response. */}
                {chat_transcript_url && (
                  <a
                    href={chat_transcript_url}
                    className="text-sm font-semibold text-indigo-600 hover:text-indigo-700"
                  >
                    Download chat transcript →
                  </a>
                )}
              </div>
            </div>
            <div className="flex items-end gap-2">
              <label className="block">
                <span className="block text-xs font-semibold tracking-wider text-gray-600 uppercase mb-1">Attendance minutes</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={minutes}
                  onChange={(e) => setMinutes(e.target.value)}
                  placeholder="optional"
                  className="px-3 py-2 text-sm rounded-lg border border-gray-200 w-32"
                />
              </label>
              <button
                type="button"
                onClick={() => submitMark('mark_attended')}
                disabled={selected.size === 0}
                className="px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                Mark attended ({selected.size})
              </button>
              <button
                type="button"
                onClick={() => submitMark('unmark_attended')}
                disabled={selected.size === 0}
                className="px-4 py-2 text-sm font-semibold rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                Unmark
              </button>
            </div>
          </div>

          {registrations.length === 0 ? (
            <p className="text-sm text-gray-500">No registrations yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wider text-gray-500 border-b border-gray-100">
                  <tr>
                    <th className="py-2 pr-3 w-8">
                      <input type="checkbox" checked={allSelected} onChange={toggleAll} disabled={activeRegs.length === 0} />
                    </th>
                    <th className="py-2 pr-3">User</th>
                    <th className="py-2 pr-3">Registered</th>
                    <th className="py-2 pr-3">Attended</th>
                    <th className="py-2 pr-3">Minutes</th>
                    <th className="py-2 pr-3">Credit awarded</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {registrations.map((r) => {
                    const cancelled = !!r.cancelled_at;
                    return (
                      <tr key={r.id} className={cancelled ? 'opacity-50' : ''}>
                        <td className="py-2 pr-3">
                          <input
                            type="checkbox"
                            checked={selected.has(r.user_id)}
                            disabled={cancelled}
                            onChange={() => toggle(r.user_id)}
                          />
                        </td>
                        <td className="py-2 pr-3">
                          <p className="font-medium text-gray-900">{r.user_name}</p>
                          <p className="text-xs text-gray-500">{r.user_email}</p>
                          {cancelled && <span className="inline-flex items-center mt-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-gray-100 text-gray-600">Cancelled {fmtDate(r.cancelled_at)}</span>}
                        </td>
                        <td className="py-2 pr-3 text-gray-600">{fmtDate(r.registered_at)}</td>
                        <td className="py-2 pr-3 text-gray-600">{r.attended ? 'Yes' : 'No'}</td>
                        <td className="py-2 pr-3 text-gray-600 tabular-nums">{r.attendance_minutes ?? '—'}</td>
                        <td className="py-2 pr-3 text-gray-600">{r.credit_awarded_at ? fmtDate(r.credit_awarded_at) : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <dt className="text-[11px] font-semibold tracking-wider text-gray-500 uppercase">{label}</dt>
      <dd className="text-gray-900 mt-0.5">{children}</dd>
    </div>
  );
}

function BroadcastBadge({ state }) {
  const styles = state === 'live'
    ? 'bg-rose-50 text-rose-700 border-rose-200'
    : state === 'ended'
      ? 'bg-gray-100 text-gray-700 border-gray-200'
      : 'bg-amber-50 text-amber-700 border-amber-200';
  const label = state === 'live' ? 'Live' : state === 'ended' ? 'Ended' : 'Idle';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${styles}`}>
      {state === 'live' && <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />}
      {label}
    </span>
  );
}

function CopyField({ label, value, hidden }) {
  const [revealed, setRevealed] = React.useState(!hidden);
  const [copied, setCopied] = React.useState(false);
  const display = hidden && !revealed ? '••••••••••••••••••••••••' : value;
  const copy = () => {
    navigator.clipboard.writeText(value || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div>
      <p className="text-[11px] font-semibold tracking-wider text-gray-500 uppercase mb-1">{label}</p>
      <div className="flex items-stretch gap-2">
        <code className="flex-1 min-w-0 truncate px-3 py-2 text-xs bg-gray-50 border border-gray-200 rounded-lg text-gray-800">{display || '—'}</code>
        {hidden && (
          <button
            type="button"
            onClick={() => setRevealed((v) => !v)}
            className="px-2 py-1 text-xs font-semibold rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 cursor-pointer"
          >
            {revealed ? 'Hide' : 'Show'}
          </button>
        )}
        <button
          type="button"
          onClick={copy}
          disabled={!value}
          className="px-2 py-1 text-xs font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 cursor-pointer"
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

const INVITE_SEARCH_MIN_LENGTH = 2;

function InvitedUsersPanel({ webinar, invitations }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Search-only typeahead: only hit the server once the user has typed enough
  // to make the result set useful. Avoids loading the whole admin list on mount.
  React.useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < INVITE_SEARCH_MIN_LENGTH) {
      setResults([]);
      setSearching(false);
      return;
    }
    const handle = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(
          `/superadmin/webinars/${webinar.id}/invitable_users?q=${encodeURIComponent(trimmed)}`,
          { headers: { Accept: 'application/json' }, credentials: 'same-origin' },
        );
        const data = res.ok ? await res.json() : [];
        setResults(Array.isArray(data) ? data : []);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [query, webinar.id]);

  const invite = (userId) => {
    router.post(
      `/superadmin/webinars/${webinar.id}/add_invitation`,
      { user_id: userId },
      { preserveScroll: true, onSuccess: () => { setQuery(''); setResults([]); } },
    );
  };

  const remove = (userId) => {
    if (!confirm('Remove this invitation? They will no longer see this webinar (existing registration is kept).')) return;
    router.delete(
      `/superadmin/webinars/${webinar.id}/invitations/${userId}`,
      { preserveScroll: true },
    );
  };

  const trimmedQuery = query.trim();
  const hasQuery = trimmedQuery.length >= INVITE_SEARCH_MIN_LENGTH;

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Invited users</h2>
        <p className="text-sm text-gray-500">
          Only invited users can see and register for this private webinar.
        </p>
      </div>

      <div className="mb-4">
        <label htmlFor="invite-search" className="block text-xs font-semibold tracking-wider text-gray-600 uppercase mb-1">
          Invite by email
        </label>
        <input
          id="invite-search"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Type at least 2 characters of an email or name…"
          autoComplete="off"
          className="block w-full rounded-lg border border-gray-200 bg-white px-3.5 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none transition"
        />
        {hasQuery && (
          <div className="mt-2 border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-56 overflow-y-auto">
            {searching && <p className="px-3 py-2 text-sm text-gray-500">Searching…</p>}
            {!searching && results.length === 0 && (
              <p className="px-3 py-2 text-sm text-gray-500">No matching administrator students.</p>
            )}
            {results.map((u) => (
              <button
                key={u.id}
                type="button"
                onClick={() => invite(u.id)}
                className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-gray-50 cursor-pointer"
              >
                <span>
                  <span className="font-medium text-gray-900">{u.name}</span>
                  <span className="block text-xs text-gray-500">{u.email}</span>
                </span>
                <span className="text-xs font-semibold text-indigo-600">Invite</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {invitations.length === 0 ? (
        <p className="text-sm text-gray-500">No one invited yet.</p>
      ) : (
        <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
          {invitations.map((i) => (
            <li key={i.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
              <span>
                <span className="font-medium text-gray-900">{i.user_name}</span>
                <span className="block text-xs text-gray-500">{i.user_email}</span>
              </span>
              <button
                type="button"
                onClick={() => remove(i.user_id)}
                className="px-2 py-1 text-xs font-semibold rounded-lg border border-rose-200 text-rose-700 hover:bg-rose-50 cursor-pointer"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StreamingPanel({ webinar }) {
  const regen = () => {
    if (!confirm('Issue a new stream key? The instructor must update their encoder.')) return;
    router.post(`/superadmin/webinars/${webinar.id}/regenerate_stream_key`, {}, { preserveScroll: true });
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Stream credentials</h2>
          <p className="text-sm text-gray-500">Broadcast from a browser, or hand the credentials below to an OBS/vMix encoder.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            href={`/superadmin/webinars/${webinar.id}/broadcast`}
            className="px-3 py-1.5 text-sm font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm"
          >
            Broadcast from browser
          </Link>
          <Link
            href={`/superadmin/webinars/${webinar.id}/produce`}
            className="px-3 py-1.5 text-sm font-semibold rounded-lg border border-violet-200 text-violet-800 hover:bg-violet-50"
          >
            Join as producer
          </Link>
          <button
            type="button"
            onClick={regen}
            className="px-3 py-1.5 text-sm font-semibold rounded-lg border border-amber-200 text-amber-800 hover:bg-amber-50 cursor-pointer"
          >
            Regenerate key
          </button>
        </div>
      </div>

      <div className="space-y-4">
        <CopyField label="RTMP ingest URL" value={webinar.rtmp_ingest_url} />
        <CopyField label="Stream key" value={webinar.stream_key} hidden />
        <CopyField label="HLS playback URL (token appended at runtime)" value={webinar.hls_playback_url} />
        {webinar.room_url && <CopyField label="In-portal room" value={webinar.room_url} />}
        {webinar.broadcast_started_at && (
          <p className="text-xs text-gray-500">
            Last live: {new Date(webinar.broadcast_started_at).toLocaleString()}
            {webinar.broadcast_ended_at ? ` → ${new Date(webinar.broadcast_ended_at).toLocaleString()}` : ' (still live)'}
          </p>
        )}
      </div>
    </div>
  );
}
